import { useCallback, useMemo } from "react";
import { CaretLeft, CaretRight, IdentificationCard } from "@phosphor-icons/react";
import { ViewSessionCard, ViewWeekCard } from "./SharedComponents";
import { DAYS, DAY_SHORT, fmtTime, groupSessionsByTimeSlot } from "./utils";

export default function ScheduleView({
  viewMode,
  viewDayIdx,
  setViewDayIdx,
  setViewMode,
  weekDates,
  mySessions,
  conflictSet,
  sharedConflictSet,
  sharedSchoolSessions = [],
  onSessionLogged,
}) {
  // Group same-(date, start, end, provider) sessions into one entry — consistent with Today.
  const groupedSessions = useMemo(() => groupSessionsByTimeSlot(mySessions), [mySessions]);

  const sessionsForDay = useCallback(
    (date) => groupedSessions.filter((s) => s.date === date).sort((a, b) => a.start_time.localeCompare(b.start_time)),
    [groupedSessions]
  );
  // Iter 31 — School-context overlay: other providers' sessions involving
  // my students. Rendered as muted "ghost" cards beneath the user's own
  // sessions so the canvas still feels owned by the provider.
  const sharedForDay = useCallback(
    (date) =>
      sharedSchoolSessions
        .filter((s) => s.date === date)
        .sort((a, b) => a.start_time.localeCompare(b.start_time)),
    [sharedSchoolSessions]
  );

  if (viewMode === "day") {
    const daySessions = sessionsForDay(weekDates[viewDayIdx]);
    const dayShared = sharedForDay(weekDates[viewDayIdx]);
    // FP3-034E — Append the concrete date next to the weekday label so
    // providers can confirm which day they're reviewing.
    const dayLabelDate = (() => {
      const iso = weekDates[viewDayIdx];
      if (!iso) return "";
      const d = new Date(iso + "T12:00:00");
      return ` · ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
    })();
    return (
      <div className="max-w-3xl" data-testid="view-day">
        <div className="flex items-center gap-1 mb-5">
          <button
            data-testid="view-prev-day"
            onClick={() => setViewDayIdx(Math.max(0, viewDayIdx - 1))}
            disabled={viewDayIdx === 0}
            className="w-7 h-7 rounded-lg border border-[#E5E3DB] flex items-center justify-center hover:bg-[#F3F1EC] disabled:opacity-30"
          >
            <CaretLeft size={14} className="text-[#6B7280]" />
          </button>
          {DAYS.map((d, i) => (
            <button
              key={d}
              data-testid={`view-day-btn-${i}`}
              onClick={() => setViewDayIdx(i)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                i === viewDayIdx ? "bg-[#1E3F33] text-white" : "text-[#6B7280] hover:bg-[#F3F1EC]"
              }`}
            >
              {DAY_SHORT[i]}
            </button>
          ))}
          <button
            data-testid="view-next-day"
            onClick={() => setViewDayIdx(Math.min(4, viewDayIdx + 1))}
            disabled={viewDayIdx === 4}
            className="w-7 h-7 rounded-lg border border-[#E5E3DB] flex items-center justify-center hover:bg-[#F3F1EC] disabled:opacity-30"
          >
            <CaretRight size={14} className="text-[#6B7280]" />
          </button>
          <span className="text-sm text-[#1F2937] font-medium heading-font ml-2" data-testid="view-day-label">
            {DAYS[viewDayIdx]}{dayLabelDate}
          </span>
        </div>
        {daySessions.length === 0 && dayShared.length === 0 ? (
          <div className="text-center py-12 text-sm text-[#6B7280]">No sessions on {DAYS[viewDayIdx]}.</div>
        ) : (
          <div className="space-y-2">
            {daySessions.map((s) => (
              <ViewSessionCard key={s.id} session={s} conflictSet={conflictSet} onLogged={onSessionLogged} />
            ))}
            {dayShared.length > 0 && (
              <div data-testid="view-day-shared-section" className="pt-3 mt-3 border-t border-dashed border-[#E5E3DB]">
                <p className="text-[10px] uppercase tracking-wider text-[#9C6B2E] font-semibold mb-2 flex items-center gap-1.5">
                  <IdentificationCard size={11} weight="duotone" />
                  School context — read only
                </p>
                <div className="space-y-2">
                  {dayShared.map((s) => (
                    <SharedSessionGhost
                      key={`shared-${s.id}`}
                      session={s}
                      sharedConflictSet={sharedConflictSet}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Week
  const today = new Date().toISOString().split("T")[0];
  // FP3-034E — Format the calendar date below each weekday name so
  // providers know exactly which day they're looking at when reviewing
  // unavailability, calendar exceptions, and service-minute windows.
  const formatHeaderDate = (iso) => {
    const d = new Date(iso + "T12:00:00");
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };
  return (
    <div className="grid grid-cols-5 gap-4" data-testid="view-week">
      {weekDates.map((date, idx) => {
        const ds = sessionsForDay(date);
        const dShared = sharedForDay(date);
        const isToday = date === today;
        return (
          <div key={date} data-testid={`view-week-col-${idx}`}>
            <div
              className={`text-center mb-3 pb-2 border-b-2 cursor-pointer ${
                isToday ? "border-[#1E3F33]" : "border-[#E5E3DB]"
              }`}
              onClick={() => {
                setViewMode("day");
                setViewDayIdx(idx);
              }}
            >
              <p className={`text-xs ${isToday ? "text-[#1E3F33] font-semibold" : "text-[#6B7280]"}`}>{DAY_SHORT[idx]}</p>
              <p className={`text-sm font-semibold heading-font ${isToday ? "text-[#1E3F33]" : "text-[#1F2937]"}`}>
                {DAYS[idx]}
              </p>
              <p
                data-testid={`view-week-col-date-${idx}`}
                className={`text-[11px] tabular-nums mt-0.5 ${
                  isToday ? "text-[#1E3F33] font-semibold" : "text-[#9B9890]"
                }`}
              >
                {formatHeaderDate(date)}
              </p>
            </div>
            <div className="space-y-2">
              {ds.length === 0 && dShared.length === 0 ? (
                <p className="text-xs text-[#6B7280] text-center py-4">No sessions</p>
              ) : (
                <>
                  {ds.map((s) => <ViewWeekCard key={s.id} session={s} conflictSet={conflictSet} onLogged={onSessionLogged} />)}
                  {dShared.map((s) => (
                    <SharedSessionGhost
                      key={`shared-${s.id}`}
                      session={s}
                      sharedConflictSet={sharedConflictSet}
                      compact
                    />
                  ))}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Iter 31 — Read-only ghost card for the school-context overlay. Visually
// muted (dashed border, striped background, neutral palette) so it doesn't
// compete with the provider's own owned sessions but still gives them the
// context they need to identify cross-provider overlaps. Never editable.
function SharedSessionGhost({ session, sharedConflictSet, compact = false }) {
  const isConflict = sharedConflictSet?.has(session.id);
  const studentNames =
    (session.students || []).map((s) => s.first_name).filter(Boolean).join(", ") ||
    "—";
  return (
    <div
      data-testid={`shared-session-${session.id}`}
      data-shared="true"
      className={`relative rounded-lg border border-dashed bg-[#F3F1EC]/40 px-3 py-2 ${
        compact ? "" : ""
      } ${
        isConflict
          ? "border-[#D9A05B] ring-1 ring-[#D9A05B]/40"
          : "border-[#cfcfc6]"
      }`}
      style={{
        backgroundImage:
          "repeating-linear-gradient(135deg, transparent 0 6px, rgba(0,0,0,0.025) 6px 7px)",
      }}
      title="Read-only school context — owned by another provider"
    >
      <div className="flex items-center justify-between gap-2 text-[11px]">
        <span className="text-[#6B7280] font-medium tabular-nums">
          {fmtTime(session.start_time)}–{fmtTime(session.end_time)}
        </span>
        {isConflict && (
          <span className="text-[10px] uppercase tracking-wider text-[#9C6B2E] font-semibold">
            shared conflict
          </span>
        )}
      </div>
      <div className="text-xs text-[#4B5563] truncate mt-0.5">{studentNames}</div>
      <div className="text-[10px] text-[#9B9890] mt-0.5 italic flex items-center gap-1">
        <IdentificationCard size={10} weight="duotone" />
        <span className="truncate">
          {session.owner_name || "Other provider"}
          {session.owner_role ? ` · ${session.owner_role}` : ""}
          {session.service_type ? ` · ${session.service_type}` : ""}
        </span>
      </div>
    </div>
  );
}

